import React from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import styled from 'styled-components'
import { IconButton } from 'react-mdl'
import ImageSelectionModal from '../../../ImageSelectionModal'
import TransformedImage from '../../../TransformedImage'
import { utils } from '../List'

const ImageContainer = styled.div`text-align: center;`
const MediaBlockContainer = styled.div`display: flex;`
const MediaBlockContentContainer = styled.div`flex-grow: 1;`
const MediaBlockImageContainer = styled.div`
  width: 5.5cm;
  margin: 0 0.5cm;
  flex-grow: 0;
  flex-shrink: 0;
`

async function handleClick({
  inMediablock,
  onChange,
  state,
  prompt,
  unitImages,
}) {
  // Convert to mediablock or insert image
  const mediaBlock = state.selection.isExpanded
  // If within a mediablock and not expanded then manage
  if (inMediablock) {
    // TODO: open management interface instead of unwrapping media block
    const transform = state.transform()

    if (state.anchorBlock.type === 'media_block') {
      transform.setBlock({ type: 'paragraph', data: {} })
    } else {
      transform.unwrapBlock('media_block')
    }
    return onChange(transform.apply())
  }

  const scopeContent = state.anchorBlock.getText()
  const response = await prompt({
    ContentComponent: props =>
      <ImageSelectionModal
        {...props}
        imageLimit={mediaBlock ? Infinity : 1}
        scopeContent={scopeContent}
        unitImages={unitImages}
      />,
    style: {
      top: '2em',
      width: 850,
    },
  })
  if (response === 'cancel') {
    return
  }

  let transform = state.transform()
  if (mediaBlock) {
    // expand selection if in list
    if (utils.isSelectionInList(transform.state)) {
      const parentList = transform.state.document
        .getAncestors(transform.state.anchorKey)
        .find(node => node.type === 'ul_list' || node.type === 'ol_list')
      if (parentList) {
        if (transform.state.selection.isBackward) {
          transform.flip()
        }
        // set selection to include the paragraph preceeding the list to ensure it is wrapped correctly
        transform.collapseToStartOf(parentList)
        transform.collapseToStartOfPreviousBlock()
        transform.extendToEndOf(parentList)
      }
    }
    // if has content then convert current block to mediablock
    transform.wrapBlock({
      type: 'media_block',
      data: {
        images: response,
      },
    })
  } else {
    // else insert image at cursor
    const [image] = response
    // insert new line for after image
    transform.collapseToEndOfNextBlock()
    transform.insertBlock('paragraph')
    transform.collapseToEndOfPreviousBlock()
    transform.insertBlock({
      type: 'image',
      data: {
        mediaId: image.mediaId,
        transforms: image.transforms,
        display: {
          width: 7.5,
        },
      },
    })
  }
  onChange(transform.apply())
}

const ImageButton = function ImageButton(
  { editorState, onChange, unitImages },
  { prompt },
) {
  const ancestors = editorState.document.getAncestors(
    editorState.selection.anchorKey,
  )
  const inMediablock = ancestors.some(({ type }) => type === 'media_block')
  return (
    <IconButton
      name="image"
      onClick={() =>
        handleClick({
          onChange,
          inMediablock,
          state: editorState,
          prompt,
          unitImages,
        })}
    />
  )
}
ImageButton.contextTypes = {
  prompt: PropTypes.func,
}

function handleImageProps({ onChange, prompt, state, node }) {
  return async () => {
    const transform = state.transform()
    const { action, output } = await prompt({
      title: 'Image properties',
      message: 'Set image properties.',
      inputs: [
        {
          type: 'number',
          initialValue: node.data.get('width'),
          label: 'Image Width',
          name: 'width',
        },
      ],
      options: ['Save'],
    })
    if (action !== 'Save') {
      return
    }
    transform.setNodeByKey(node.key, {
      data: node.data.setIn(['display', 'width'], output.width),
    })
    onChange(transform.apply())
  }
}

const plugin = ({ onChange }) => {
  const Image = function Image({ state, node }, { prompt }) {
    let display = new Map(node.data.get('display')).toJS()
    return (
      <ImageContainer
        onDoubleClick={handleImageProps({ onChange, prompt, state, node })}>
        <TransformedImage
          image={{
            mediaId: node.data.get('mediaId'),
            transforms: node.data.get('transforms'),
            display,
            margin: '0.1cm',
          }}
        />
      </ImageContainer>
    )
  }
  Image.contextTypes = {
    prompt: PropTypes.func,
  }
  function MediaBlock({ children, node }) {
    return (
      <MediaBlockContainer>
        <MediaBlockContentContainer>
          {children}
        </MediaBlockContentContainer>
        <MediaBlockImageContainer>
          {node.data.get('images').map((image, i) => {
            return (
              <TransformedImage
                key={i}
                image={image}
                style={{ width: '5.5cm' }}
                role="presentation"
              />
            )
          })}
        </MediaBlockImageContainer>
      </MediaBlockContainer>
    )
  }

  return {
    schema: {
      nodes: {
        image: Image,
        media_block: MediaBlock,
      },
    },
  }
}

export { plugin as ImagePlugin, ImageButton }



// WEBPACK FOOTER //
// ./src/components/Editor/plugins/Image/index.js